/**
 * Fees Configuration
 * 
 * Network fee structure and pricing management:
 * - View and configure pricing tiers
 * - Cost calculator for usage estimation
 * - USDC balance and deposit management
 * - Usage-based billing overview
 */

import { useState, useEffect, useMemo } from 'react';
import { useDebug } from '../context/DebugContext';
import './Fees.css';

// SVG Icons
const Icons = {
  DollarSign: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
    </svg>
  ),
  Calculator: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <line x1="8" y1="6" x2="16" y2="6" />
      <line x1="8" y1="10" x2="8" y2="10.01" />
      <line x1="12" y1="10" x2="12" y2="10.01" />
      <line x1="16" y1="10" x2="16" y2="10.01" />
      <line x1="8" y1="14" x2="8" y2="14.01" />
      <line x1="12" y1="14" x2="12" y2="14.01" />
      <line x1="16" y1="14" x2="16" y2="14.01" />
      <line x1="8" y1="18" x2="8" y2="18.01" />
      <line x1="12" y1="18" x2="16" y2="18" />
    </svg>
  ),
  Wallet: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 4H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2z" />
      <path d="M16 12a2 2 0 100-4 2 2 0 000 4z" fill="currentColor" />
    </svg>
  ),
  CheckCircle: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="10" cy="10" r="8" />
      <path d="M6.5 10l2.5 2.5 5-5" />
    </svg>
  ),
  Info: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="8" cy="8" r="6" />
      <line x1="8" y1="7" x2="8" y2="11" />
      <circle cx="8" cy="5" r="0.5" fill="currentColor" />
    </svg>
  ),
  USDC: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" fill="#2775CA" />
      <path d="M12 6v1.5M12 16.5V18M15 12c0-1.65-1.35-3-3-3s-3 1.35-3 3 1.35 3 3 3 3-1.35 3-3z" stroke="white" strokeWidth="1.5" fill="none" />
      <text x="12" y="14" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">$</text>
    </svg>
  ),
  Zap: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="11,1 5,11 10,11 9,19 15,9 10,9 11,1" />
    </svg>
  ),
  Database: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <ellipse cx="10" cy="5" rx="7" ry="3" />
      <path d="M3 5v10c0 1.66 3.13 3 7 3s7-1.34 7-3V5" />
      <path d="M3 10c0 1.66 3.13 3 7 3s7-1.34 7-3" />
    </svg>
  ),
  Globe: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="10" cy="10" r="8" />
      <path d="M2 10h16M10 2c2.5 2.5 4 5 4 8s-1.5 5.5-4 8c-2.5-2.5-4-5-4-8s1.5-5.5 4-8z" />
    </svg>
  ),
  ExternalLink: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 9v4a1 1 0 01-1 1H3a1 1 0 01-1-1V5a1 1 0 011-1h4M8 8l6-6M10 2h4v4" />
    </svg>
  ),
};

// Pricing tier definitions
interface PricingTier {
  id: 'starter' | 'professional' | 'enterprise';
  name: string;
  basePrice: number;
  includedCU: number;
  overageRate: number; // per 1K CU
  storageRate: number; // per GB/month
  bandwidthRate: number; // per GB
  features: string[];
  recommended?: boolean;
}

const PRICING_TIERS: PricingTier[] = [
  {
    id: 'starter',
    name: 'Starter',
    basePrice: 0,
    includedCU: 100_000,
    overageRate: 0.10,
    storageRate: 0.10,
    bandwidthRate: 0.05,
    features: [
      '100K Compute Units/month',
      '1 GB storage included',
      'Community support',
      'Basic analytics',
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    basePrice: 99,
    includedCU: 1_000_000,
    overageRate: 0.08,
    storageRate: 0.08,
    bandwidthRate: 0.04,
    features: [
      '1M Compute Units/month',
      '10 GB storage included',
      'Priority support',
      'Advanced analytics',
      'Custom circuits',
      'Team collaboration',
    ],
    recommended: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    basePrice: -1, // Custom
    includedCU: -1, // Custom
    overageRate: 0.05, // Volume discount
    storageRate: 0.05,
    bandwidthRate: 0.02,
    features: [
      'Custom CU allocation',
      'Unlimited storage',
      'Dedicated support',
      'SLA guarantees',
      'Custom integrations',
      'On-premise option',
      'Audit logs',
    ],
  },
];

interface AccountBalance {
  usdc: number;
  pendingDeposits: number;
  lastUpdated: string;
}

interface UsageMetrics {
  computeUnits: number;
  storageGB: number;
  bandwidthGB: number;
  currentTier: 'starter' | 'professional' | 'enterprise';
}

type TabId = 'pricing' | 'calculator' | 'balance' | 'settings';

export default function Fees() {
  const { addEvent } = useDebug();
  const [activeTab, setActiveTab] = useState<TabId>('pricing');
  const [balance, setBalance] = useState<AccountBalance | null>(null);
  const [usage, setUsage] = useState<UsageMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDepositModal, setShowDepositModal] = useState(false);
  
  // Calculator state
  const [calcCU, setCalcCU] = useState(500_000);
  const [calcStorage, setCalcStorage] = useState(5);
  const [calcBandwidth, setCalcBandwidth] = useState(10);
  const [calcTier, setCalcTier] = useState<'starter' | 'professional' | 'enterprise'>('professional');
  
  useEffect(() => {
    addEvent({
      source: 'console',
      category: 'navigation',
      message: 'Viewed Fees page',
    });
    
    fetchData();
  }, [addEvent]);
  
  const fetchData = async () => {
    setLoading(true);
    
    try {
      const edgeUrl = import.meta.env.VITE_EDGE_URL || 'https://edge.estream.dev';
      
      // Fetch balance
      const balanceResponse = await fetch(`${edgeUrl}/api/tenant/balance`);
      if (balanceResponse.ok) {
        const data = await balanceResponse.json();
        setBalance(data);
      } else {
        setMockBalance();
      }
      
      // Fetch usage
      const usageResponse = await fetch(`${edgeUrl}/api/tenant/usage`);
      if (usageResponse.ok) {
        const data = await usageResponse.json();
        setUsage(data);
      } else {
        setMockUsage();
      }
    } catch {
      setMockBalance();
      setMockUsage();
    } finally {
      setLoading(false);
    }
  };
  
  const setMockBalance = () => {
    setBalance({
      usdc: 247.50,
      pendingDeposits: 0,
      lastUpdated: new Date().toISOString(),
    });
  };
  
  const setMockUsage = () => {
    setUsage({
      computeUnits: 456_789,
      storageGB: 3.2,
      bandwidthGB: 8.5,
      currentTier: 'professional',
    });
  };
  
  // Calculate estimated cost based on inputs
  const estimatedCost = useMemo(() => {
    const tier = PRICING_TIERS.find(t => t.id === calcTier);
    if (!tier || tier.basePrice < 0) {
      return { base: 0, compute: 0, storage: 0, bandwidth: 0, total: 0, isCustom: true };
    }
    
    const base = tier.basePrice;
    const overageCU = Math.max(0, calcCU - tier.includedCU);
    const compute = (overageCU / 1000) * tier.overageRate;
    const storage = calcStorage * tier.storageRate;
    const bandwidth = calcBandwidth * tier.bandwidthRate;
    const total = base + compute + storage + bandwidth;
    
    return { base, compute, storage, bandwidth, total, isCustom: false };
  }, [calcCU, calcStorage, calcBandwidth, calcTier]);
  
  // Current month cost based on actual usage
  const currentCost = useMemo(() => {
    if (!usage) return null;
    
    const tier = PRICING_TIERS.find(t => t.id === usage.currentTier);
    if (!tier || tier.basePrice < 0) return null;
    
    const base = tier.basePrice;
    const overageCU = Math.max(0, usage.computeUnits - tier.includedCU);
    const compute = (overageCU / 1000) * tier.overageRate;
    const storage = usage.storageGB * tier.storageRate;
    const bandwidth = usage.bandwidthGB * tier.bandwidthRate;
    const total = base + compute + storage + bandwidth;
    
    return { base, compute, storage, bandwidth, total };
  }, [usage]);
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };
  
  const formatNumber = (n: number) => n.toLocaleString();
  
  if (loading) {
    return (
      <div className="fees-page loading-state">
        <div className="spinner" />
        <span>Loading fee configuration...</span>
      </div>
    );
  }
  
  return (
    <div className="fees-page">
      {/* Header */}
      <div className="fees-header">
        <div className="header-content">
          <h1>Fees & Pricing</h1>
          <p className="subtitle">
            Configure network fees and manage your USDC balance
          </p>
        </div>
        
        {/* Balance Quick View */}
        {balance && (
          <div className="balance-quick">
            <div className="balance-icon">
              <Icons.USDC />
            </div>
            <div className="balance-info">
              <span className="balance-label">USDC Balance</span>
              <span className="balance-value">{formatCurrency(balance.usdc)}</span>
            </div>
            <button className="btn-primary" onClick={() => setShowDepositModal(true)}>
              Deposit
            </button>
          </div>
        )}
      </div>
      
      {/* Tabs */}
      <div className="tab-nav">
        {(['pricing', 'calculator', 'balance', 'settings'] as TabId[]).map(tab => (
          <button
            key={tab}
            className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>
      
      {/* Content */}
      <div className="fees-content">
        {activeTab === 'pricing' && (
          <div className="pricing-content">
            {/* Pricing Formula */}
            <section className="card formula-card">
              <h2>Pricing Formula</h2>
              <div className="formula">
                <code>
                  Monthly Cost = Base Fee + (Compute Units × Rate) + (Storage GB × Rate) + (Bandwidth GB × Rate)
                </code>
              </div>
              <div className="formula-legend">
                <div className="legend-item">
                  <Icons.Zap />
                  <span><strong>Compute Units (CU)</strong> - Circuit executions, crypto operations</span>
                </div>
                <div className="legend-item">
                  <Icons.Database />
                  <span><strong>Storage</strong> - ESLite data, circuit state</span>
                </div>
                <div className="legend-item">
                  <Icons.Globe />
                  <span><strong>Bandwidth</strong> - Network egress</span>
                </div>
              </div>
            </section>
            
            {/* Pricing Tiers */}
            <div className="pricing-tiers">
              {PRICING_TIERS.map(tier => (
                <div 
                  key={tier.id} 
                  className={`tier-card ${tier.recommended ? 'recommended' : ''} ${usage?.currentTier === tier.id ? 'current' : ''}`}
                >
                  {tier.recommended && <span className="recommended-badge">Recommended</span>}
                  {usage?.currentTier === tier.id && <span className="current-badge">Current Plan</span>}
                  
                  <h3 className="tier-name">{tier.name}</h3>
                  
                  <div className="tier-price">
                    {tier.basePrice >= 0 ? (
                      <>
                        <span className="price">{formatCurrency(tier.basePrice)}</span>
                        <span className="period">/month</span>
                      </>
                    ) : (
                      <span className="price custom">Custom</span>
                    )}
                  </div>
                  
                  <div className="tier-included">
                    {tier.includedCU >= 0 ? (
                      <span>{formatNumber(tier.includedCU)} CU/month included</span>
                    ) : (
                      <span>Custom allocation</span>
                    )}
                  </div>
                  
                  <div className="tier-rates">
                    <div className="rate">
                      <span className="rate-label">Overage</span>
                      <span className="rate-value">{formatCurrency(tier.overageRate)}/1K CU</span>
                    </div>
                    <div className="rate">
                      <span className="rate-label">Storage</span>
                      <span className="rate-value">{formatCurrency(tier.storageRate)}/GB</span>
                    </div>
                    <div className="rate">
                      <span className="rate-label">Bandwidth</span>
                      <span className="rate-value">{formatCurrency(tier.bandwidthRate)}/GB</span>
                    </div>
                  </div>
                  
                  <ul className="tier-features">
                    {tier.features.map((feature, i) => (
                      <li key={i}>
                        <Icons.CheckCircle />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button 
                    className={`btn-tier ${usage?.currentTier === tier.id ? 'btn-current' : 'btn-select'}`}
                    disabled={usage?.currentTier === tier.id}
                  >
                    {usage?.currentTier === tier.id ? 'Current Plan' : tier.id === 'enterprise' ? 'Contact Sales' : 'Select Plan'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {activeTab === 'calculator' && (
          <div className="calculator-content">
            <section className="card">
              <div className="card-header">
                <h2><Icons.Calculator /> Cost Calculator</h2>
              </div>
              
              <div className="calculator-grid">
                {/* Inputs */}
                <div className="calc-inputs">
                  <div className="form-group">
                    <label>Pricing Tier</label>
                    <select 
                      value={calcTier} 
                      onChange={e => setCalcTier(e.target.value as typeof calcTier)}
                    >
                      <option value="starter">Starter (Free base)</option>
                      <option value="professional">Professional ($99/mo)</option>
                      <option value="enterprise">Enterprise (Custom)</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>
                      Compute Units (CU) / month
                      <span className="input-hint">{formatNumber(calcCU)} CU</span>
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="10000000"
                      step="10000"
                      value={calcCU}
                      onChange={e => setCalcCU(Number(e.target.value))}
                    />
                    <div className="range-labels">
                      <span>0</span>
                      <span>10M</span>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>
                      Storage (GB)
                      <span className="input-hint">{calcStorage} GB</span>
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={calcStorage}
                      onChange={e => setCalcStorage(Number(e.target.value))}
                    />
                    <div className="range-labels">
                      <span>0</span>
                      <span>100 GB</span>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>
                      Bandwidth (GB)
                      <span className="input-hint">{calcBandwidth} GB</span>
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="500"
                      step="5"
                      value={calcBandwidth}
                      onChange={e => setCalcBandwidth(Number(e.target.value))}
                    />
                    <div className="range-labels">
                      <span>0</span>
                      <span>500 GB</span>
                    </div>
                  </div>
                </div>
                
                {/* Results */}
                <div className="calc-results">
                  {estimatedCost.isCustom ? (
                    <div className="custom-pricing">
                      <h3>Enterprise Pricing</h3>
                      <p>Contact our sales team for custom pricing tailored to your needs.</p>
                      <button className="btn-primary">Contact Sales</button>
                    </div>
                  ) : (
                    <>
                      <h3>Estimated Monthly Cost</h3>
                      
                      <div className="cost-breakdown">
                        <div className="cost-line">
                          <span>Base Plan Fee</span>
                          <span>{formatCurrency(estimatedCost.base)}</span>
                        </div>
                        <div className="cost-line">
                          <span>Compute Overage</span>
                          <span>{formatCurrency(estimatedCost.compute)}</span>
                        </div>
                        <div className="cost-line">
                          <span>Storage</span>
                          <span>{formatCurrency(estimatedCost.storage)}</span>
                        </div>
                        <div className="cost-line">
                          <span>Bandwidth</span>
                          <span>{formatCurrency(estimatedCost.bandwidth)}</span>
                        </div>
                        <div className="cost-line total">
                          <span>Total</span>
                          <span>{formatCurrency(estimatedCost.total)}</span>
                        </div>
                      </div>
                      
                      <div className="cost-note">
                        <Icons.Info />
                        <span>This is an estimate. Actual costs may vary based on usage.</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </section>
            
            {/* Current Usage */}
            {currentCost && usage && (
              <section className="card">
                <div className="card-header">
                  <h2>Current Month (So Far)</h2>
                </div>
                
                <div className="current-usage-grid">
                  <div className="usage-stat">
                    <span className="stat-value">{formatNumber(usage.computeUnits)}</span>
                    <span className="stat-label">Compute Units</span>
                  </div>
                  <div className="usage-stat">
                    <span className="stat-value">{usage.storageGB.toFixed(1)} GB</span>
                    <span className="stat-label">Storage</span>
                  </div>
                  <div className="usage-stat">
                    <span className="stat-value">{usage.bandwidthGB.toFixed(1)} GB</span>
                    <span className="stat-label">Bandwidth</span>
                  </div>
                  <div className="usage-stat highlight">
                    <span className="stat-value">{formatCurrency(currentCost.total)}</span>
                    <span className="stat-label">Current Cost</span>
                  </div>
                </div>
              </section>
            )}
          </div>
        )}
        
        {activeTab === 'balance' && (
          <div className="balance-content">
            {balance && (
              <>
                {/* Balance Card */}
                <section className="card balance-card-large">
                  <div className="balance-display">
                    <div className="balance-icon-large">
                      <Icons.USDC />
                    </div>
                    <div className="balance-details">
                      <span className="balance-amount">{formatCurrency(balance.usdc)}</span>
                      <span className="balance-label-lg">Available USDC Balance</span>
                    </div>
                  </div>
                  
                  <div className="balance-actions">
                    <button className="btn-primary btn-lg" onClick={() => setShowDepositModal(true)}>
                      Deposit USDC
                    </button>
                    <button className="btn-secondary btn-lg" disabled>
                      Withdraw
                    </button>
                  </div>
                  
                  {balance.pendingDeposits > 0 && (
                    <div className="pending-deposits">
                      <span>Pending deposits: {formatCurrency(balance.pendingDeposits)}</span>
                    </div>
                  )}
                </section>
                
                {/* Payment Methods */}
                <section className="card">
                  <div className="card-header">
                    <h2>Payment Methods</h2>
                  </div>
                  
                  <div className="payment-methods">
                    <div className="payment-method active">
                      <div className="method-icon">
                        <Icons.USDC />
                      </div>
                      <div className="method-info">
                        <span className="method-name">USDC (USD Coin)</span>
                        <span className="method-desc">Pay with stablecoin on Solana</span>
                      </div>
                      <span className="method-badge active">Active</span>
                    </div>
                    
                    <div className="payment-method coming-soon">
                      <div className="method-icon">
                        <Icons.DollarSign />
                      </div>
                      <div className="method-info">
                        <span className="method-name">eStream Token</span>
                        <span className="method-desc">Pay with native token (coming soon)</span>
                      </div>
                      <span className="method-badge">Coming Soon</span>
                    </div>
                    
                    <div className="payment-method coming-soon">
                      <div className="method-icon">
                        <Icons.Wallet />
                      </div>
                      <div className="method-info">
                        <span className="method-name">Credit Card (Stripe)</span>
                        <span className="method-desc">Traditional payment (coming soon)</span>
                      </div>
                      <span className="method-badge">Coming Soon</span>
                    </div>
                  </div>
                </section>
                
                {/* Transaction History */}
                <section className="card">
                  <div className="card-header">
                    <h2>Transaction History</h2>
                  </div>
                  <div className="transaction-list">
                    <p className="empty-state">No transactions yet</p>
                  </div>
                </section>
              </>
            )}
          </div>
        )}
        
        {activeTab === 'settings' && (
          <div className="settings-content">
            <section className="card">
              <div className="card-header">
                <h2>Fee Settings</h2>
              </div>
              
              <div className="settings-list">
                <div className="setting-item">
                  <div className="setting-info">
                    <span className="setting-name">Auto-replenish Balance</span>
                    <span className="setting-desc">Automatically deposit when balance falls below threshold</span>
                  </div>
                  <label className="toggle">
                    <input type="checkbox" disabled />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                
                <div className="setting-item">
                  <div className="setting-info">
                    <span className="setting-name">Low Balance Alerts</span>
                    <span className="setting-desc">Get notified when balance drops below $50</span>
                  </div>
                  <label className="toggle">
                    <input type="checkbox" defaultChecked />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                
                <div className="setting-item">
                  <div className="setting-info">
                    <span className="setting-name">Usage Limit Alerts</span>
                    <span className="setting-desc">Alert at 80% of plan limits</span>
                  </div>
                  <label className="toggle">
                    <input type="checkbox" defaultChecked />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                
                <div className="setting-item">
                  <div className="setting-info">
                    <span className="setting-name">Monthly Invoice Email</span>
                    <span className="setting-desc">Receive invoice summary each month</span>
                  </div>
                  <label className="toggle">
                    <input type="checkbox" defaultChecked />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </section>
            
            <section className="card">
              <div className="card-header">
                <h2>Billing Contact</h2>
              </div>
              
              <div className="billing-contact">
                <div className="form-group">
                  <label>Billing Email</label>
                  <input type="email" defaultValue="billing@example.com" />
                </div>
                <div className="form-group">
                  <label>Company Name</label>
                  <input type="text" defaultValue="Alpha Devnet Organization" />
                </div>
                <button className="btn-primary">Save Changes</button>
              </div>
            </section>
          </div>
        )}
      </div>
      
      {/* Deposit Modal */}
      {showDepositModal && (
        <DepositModal onClose={() => setShowDepositModal(false)} />
      )}
    </div>
  );
}

// Deposit Modal Component
function DepositModal({ onClose }: { onClose: () => void }) {
  const [amount, setAmount] = useState('100');
  const [step, setStep] = useState<'amount' | 'confirm' | 'processing' | 'success'>('amount');
  
  const presetAmounts = [50, 100, 250, 500, 1000];
  
  const handleDeposit = async () => {
    setStep('processing');
    // Simulate deposit processing
    await new Promise(resolve => setTimeout(resolve, 2000));
    setStep('success');
  };
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal deposit-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {step === 'success' ? 'Deposit Complete' : 'Deposit USDC'}
          </h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        {step === 'amount' && (
          <>
            <div className="modal-body">
              <div className="deposit-presets">
                {presetAmounts.map(preset => (
                  <button
                    key={preset}
                    className={`preset-btn ${amount === String(preset) ? 'active' : ''}`}
                    onClick={() => setAmount(String(preset))}
                  >
                    ${preset}
                  </button>
                ))}
              </div>
              
              <div className="form-group">
                <label>Custom Amount (USDC)</label>
                <div className="input-with-prefix">
                  <span className="input-prefix">$</span>
                  <input
                    type="number"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    min="10"
                    step="10"
                  />
                </div>
              </div>
              
              <div className="deposit-info">
                <p>
                  <Icons.Info />
                  Deposits are processed on Solana. You'll need a wallet with USDC.
                </p>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn-secondary" onClick={onClose}>Cancel</button>
              <button 
                className="btn-primary" 
                onClick={() => setStep('confirm')}
                disabled={!amount || Number(amount) < 10}
              >
                Continue
              </button>
            </div>
          </>
        )}
        
        {step === 'confirm' && (
          <>
            <div className="modal-body">
              <div className="confirm-details">
                <div className="confirm-amount">
                  <span>You're depositing</span>
                  <strong>${Number(amount).toFixed(2)} USDC</strong>
                </div>
                
                <div className="confirm-wallet">
                  <span>To account</span>
                  <code>tenant-alpha-001</code>
                </div>
                
                <p className="confirm-note">
                  Connect your Solana wallet to complete the deposit.
                </p>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setStep('amount')}>Back</button>
              <button className="btn-primary" onClick={handleDeposit}>
                Connect Wallet & Deposit
              </button>
            </div>
          </>
        )}
        
        {step === 'processing' && (
          <div className="modal-body">
            <div className="processing-state">
              <div className="spinner spinner-lg" />
              <p>Processing deposit...</p>
            </div>
          </div>
        )}
        
        {step === 'success' && (
          <>
            <div className="modal-body">
              <div className="success-state">
                <div className="success-icon">✓</div>
                <h4>Deposit Successful!</h4>
                <p>${Number(amount).toFixed(2)} USDC has been added to your account.</p>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn-primary" onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
